import { Vec3 } from '../../engine/math/vec3';
import { clamp, DEG, damp, wrapAngle } from '../../engine/math/easing';
import { SpringF } from '../../engine/math/spring';
import type { Ctx } from '../Ctx';

/**
 * Over-the-shoulder camera (§7): pivot at the neck, boom 3.2 m, lateral 0.55 m, +0.3 m up.
 * Sphere-casts the boom and eases back out; position lags the pivot with 0.08 s smoothing.
 */
export class ThirdPersonCamera {
  yaw = 0; // forward = +Z, matching the player's initial facing
  pitch = 8 * DEG;
  sensitivity = 0.0022;
  boomLength = 3.2;
  lateral = 0.55;
  height = 0.3;
  pivotHeight = 1.45;
  baseFov = 70;
  readonly fovSpring = SpringF.fromFreq(70, 2.5, 0.6);
  private currentBoom = 3.2;
  private readonly pivot = new Vec3();
  private readonly smoothPivot = new Vec3();
  private readonly desired = new Vec3();
  private readonly pos = new Vec3();
  private shakeAmt = 0;
  private shakeT = 0;
  private shakeDur = 0.3;
  private readonly shakeOff = new Vec3();
  /** Extra lateral offset (e.g. lantern pushes camera left). */
  lateralExtra = 0;
  /** Camera-facing basis vectors, updated every frame. */
  readonly forward = new Vec3(0, 0, 1);
  readonly right = new Vec3(1, 0, 0);
  /** Distance from pivot to camera after collision, for body fading. */
  boomNow = 3.2;
  private first = true;

  constructor(private readonly ctx: Ctx) {}

  /** Apply mouse look. Call from the sim step. */
  look(dx: number, dy: number): void {
    this.yaw = wrapAngle(this.yaw - dx * this.sensitivity);
    this.pitch = clamp(this.pitch + dy * this.sensitivity, -35 * DEG, 60 * DEG);
  }

  shake(amount: number, duration = 0.3): void {
    this.shakeAmt = Math.max(this.shakeAmt, amount);
    this.shakeT = 0; this.shakeDur = duration;
  }
  fovKick(delta: number): void { this.fovSpring.value = this.baseFov + delta; }

  /** Yaw-only forward vector (for movement). */
  flatForward(out = new Vec3()): Vec3 { return out.set(Math.sin(this.yaw), 0, Math.cos(this.yaw)); }
  flatRight(out = new Vec3()): Vec3 { return out.set(-Math.cos(this.yaw), 0, Math.sin(this.yaw)); }

  snapTo(playerPos: Vec3): void {
    this.pivot.copy(playerPos); this.pivot.y += this.pivotHeight;
    this.smoothPivot.copy(this.pivot);
    this.first = false;
    this.update(playerPos, 1 / 60, true);
  }

  update(playerPos: Vec3, dt: number, instant = false): void {
    const cam = this.ctx.camera;
    this.pivot.copy(playerPos); this.pivot.y += this.pivotHeight;
    if (this.first || instant) { this.smoothPivot.copy(this.pivot); this.first = false; }
    else {
      this.smoothPivot.x = damp(this.smoothPivot.x, this.pivot.x, 0.055, dt);
      this.smoothPivot.y = damp(this.smoothPivot.y, this.pivot.y, 0.055, dt);
      this.smoothPivot.z = damp(this.smoothPivot.z, this.pivot.z, 0.055, dt);
    }
    // basis
    const cp = Math.cos(this.pitch), sp = Math.sin(this.pitch);
    this.forward.set(Math.sin(this.yaw) * cp, -sp, Math.cos(this.yaw) * cp);
    this.right.set(-Math.cos(this.yaw), 0, Math.sin(this.yaw));
    const up = _up.copy(this.right).cross(this.forward).normalize();

    // shoulder anchor: pivot + right*lateral + up*height
    const lat = this.lateral + this.lateralExtra;
    const anchor = _anchor.copy(this.smoothPivot).addScaled(this.right, lat).addScaled(up, this.height);
    this.desired.copy(anchor).addScaled(this.forward, -this.boomLength);
    // collision: sphere-cast from anchor back to desired
    const t = this.ctx.statics.sphereCast(anchor, this.desired, 0.22);
    const hitLen = Math.max(0.35, t * this.boomLength - (t < 1 ? 0.2 : 0));
    if (hitLen < this.currentBoom || instant) this.currentBoom = hitLen;
    else this.currentBoom = Math.min(hitLen, this.currentBoom + 4 * dt);
    this.boomNow = this.currentBoom;
    this.pos.copy(anchor).addScaled(this.forward, -this.currentBoom);

    // shake
    if (this.shakeT < this.shakeDur && this.shakeAmt > 0) {
      this.shakeT += dt;
      const k = 1 - this.shakeT / this.shakeDur;
      const a = this.shakeAmt * k * k;
      this.shakeOff.set((Math.random() - 0.5) * a, (Math.random() - 0.5) * a, 0);
    } else { this.shakeOff.set(0, 0, 0); this.shakeAmt = 0; }

    cam.position.copy(this.pos).addScaled(this.right, this.shakeOff.x).addScaled(up, this.shakeOff.y);
    cam.target.copy(cam.position).add(this.forward);
    cam.up.set(0, 1, 0);
    this.fovSpring.target = this.baseFov;
    cam.fov = this.fovSpring.update(dt);
  }
}
const _up = new Vec3();
const _anchor = new Vec3();
